// frontend/src/features/myTask/components/Filters/TeamAccordion.js
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";
import Avatar from "../../../../components/Avatar";
import { createAvatarName } from "../../../../utils";
import { useTeamAccordionState } from "../../hooks/useTeamAccordionState";

const TeamAccordion = React.memo(
  function TeamAccordion({
    team,
    user,
    expandedPanel,
    handleExpandAccordianChange,
    setTeam,
    selectedUserIds,
    syncSelectedUsers,
    filteredItems,
    searchQuery,
  }) {
    const {
      handleChange,
      handleTeamCheckboxClick,
      handleUserClick,
      selectedUserIdSet,
      shouldBeExpanded,
      teamUsers,
    } = useTeamAccordionState({
      team,
      user,
      expandedPanel,
      handleExpandAccordianChange,
      setTeam,
      selectedUserIds,
      syncSelectedUsers,
      filteredItems,
      searchQuery,
    });

    return (
      <Accordion
        expanded={shouldBeExpanded}
        onChange={handleChange}
        className="team-accordion">
        <Tooltip
          title={searchQuery && shouldBeExpanded ? "Click to collapse" : ""}
          placement="top"
          arrow
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            disableGutters
            className="accordion-summary">
            <Box onClick={(event) => event.stopPropagation()}>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={
                    teamUsers.length > 0 &&
                    teamUsers.every((teamUser) =>
                      selectedUserIdSet.has(String(teamUser.user_id))
                    )
                  }
                  onClick={handleTeamCheckboxClick}
                  onChange={() => { }}
                />
                <span className="checkmark"></span>
              </label>
            </Box>
            <Box className="team-name-box">
              <Typography>{team.team}</Typography>
            </Box>
          </AccordionSummary>
        </Tooltip>
        <AccordionDetails className="accordion-details">
          {teamUsers.map((el, index) => {
            return (
              filteredItems.some((user) => user.user_id === el.user_id) && (
                <CustomMenuItem
                  key={`user-avatar-dropdown-admin-assign-${el.user_id}`}
                  onClick={() => handleUserClick(el)}>
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      id={`user-check-box-${el.user_id}`}
                      checked={selectedUserIdSet.has(String(el.user_id))}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={() => {}}
                    />
                    <span className="checkmark"></span>
                    <Avatar
                      key={`user-avatar-${el.user_id}`}
                      username={`${el.first_name} ${el.last_name}`}
                      user_image={el.user_image}
                      title={`${el.first_name} ${el.last_name}`}
                      className="user-avatar-dropdown user-avatar mr-8">
                      {createAvatarName(`${el.first_name} ${el.last_name}`)}
                    </Avatar>
                    {`${el.first_name} ${el.last_name}`}
                  </label>
                </CustomMenuItem>
              )
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  },
  (prevProps, nextProps) => {
    // For React.memo, return true to prevent re-render, false to allow re-render
    // We want to re-render when any of the props change

    // Check if any of the props have changed
    const teamChanged =
      JSON.stringify(prevProps.team) !== JSON.stringify(nextProps.team);
    const userChanged =
      JSON.stringify(prevProps.user) !== JSON.stringify(nextProps.user);
    const expandedPanelChanged =
      JSON.stringify(prevProps.expandedPanel) !==
      JSON.stringify(nextProps.expandedPanel);
    const searchQueryChanged = prevProps.searchQuery !== nextProps.searchQuery;
    const filteredItemsChanged =
      JSON.stringify(prevProps.filteredItems) !==
      JSON.stringify(nextProps.filteredItems);
    const selectedUserIdsChanged =
      JSON.stringify(prevProps.selectedUserIds) !==
      JSON.stringify(nextProps.selectedUserIds);

    // Return false to trigger re-render when any prop has changed
    return (
      !teamChanged &&
      !userChanged &&
      !expandedPanelChanged &&
      !searchQueryChanged &&
      !filteredItemsChanged &&
      !selectedUserIdsChanged
    );
  }
);

export default TeamAccordion;
